import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { ServiceModal } from '@/components/ServiceModal';
import { openWhatsApp } from '@/lib/whatsapp';
import { useStore } from '@/contexts/StoreContext';
import {
  Phone,
  MessageCircle,
  MapPin,
  Clock,
  Mail,
  Instagram,
  Facebook,
  Youtube
} from 'lucide-react';

import googlePlay from '@/assets/google-play.png';
import appStore from '@/assets/app-store.png';
import mobiautoIcon from '@/assets/mobiauto_icon.png';
import { StoreFooterIcon } from '@/components/StoreFooterIcon';
import { hasAppStoreLink as hasResolvedAppStoreLink, resolveAppStoreUrl } from '@/lib/appStoreLinks';
import './styles.css';

interface FooterProps {
  config: any;
}

export const Footer = ({ config }: FooterProps) => {
  const { selectedStore } = useStore();
  const store = selectedStore || config.stores[0];
  const { colors } = useTheme();
  const primaryColor = colors.primary;
  const [selectedService, setSelectedService] = useState<any>(null);
  const hasGooglePlayLink = !!config.googlePlay?.url?.trim();
  const appStoreUrl = resolveAppStoreUrl(config.appStore);
  const hasAppStoreLink = hasResolvedAppStoreLink(config.appStore);

  const formatOpeningHours = () => {
    const hours = store.openingHours?.default;
    if (!hours) return [];

    const daysMap: { [key: string]: string } = {
      sunday: 'Domingo',
      monday: 'Segunda-feira',
      tuesday: 'Terça-feira',
      wednesday: 'Quarta-feira',
      thursday: 'Quinta-feira',
      friday: 'Sexta-feira',
      saturday: 'Sábado',
    };

    const weekDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const formattedHours: { day: string; time: string }[] = [];

    const weekdayHours = weekDays.map((day) => hours[day]);
    const allWeekdaysSame = weekdayHours.every(
      (h, i, arr) => h?.from === arr[0]?.from && h?.to === arr[0]?.to
    );

    if (allWeekdaysSame && hours.monday?.from && hours.monday?.to) {
      formattedHours.push({
        day: 'Segunda a Sexta',
        time: `${hours.monday.from} - ${hours.monday.to}`,
      });
    } else {
      weekDays.forEach((day) => {
        if (hours[day]?.from && hours[day]?.to) {
          formattedHours.push({
            day: daysMap[day],
            time: `${hours[day].from} - ${hours[day].to}`,
          });
        }
      });
    }

    if (hours.saturday?.from && hours.saturday?.to) {
      formattedHours.push({
        day: 'Sábado',
        time: `${hours.saturday.from} - ${hours.saturday.to}`,
      });
    } else if (!hours.saturday?.from) {
      formattedHours.push({ day: 'Sábado', time: 'Fechado' });
    }

    if (hours.sunday?.from && hours.sunday?.to) {
      formattedHours.push({
        day: 'Domingo',
        time: `${hours.sunday.from} - ${hours.sunday.to}`,
      });
    } else if (!hours.sunday?.from) {
      formattedHours.push({ day: 'Domingo', time: 'Fechado' });
    }

    return formattedHours;
  };

  const openingHours = formatOpeningHours();

  const quickLinks = [
    { label: 'Início', href: '/' },
    { label: 'Estoque', href: '/estoque' },
    { label: 'Zero KM', href: '/estoque?type=zerokm' },
    { label: 'Seminovos', href: '/estoque?type=seminovo' },
    { label: 'Favoritos', href: '/favoritos' },
    { label: 'Serviços', href: '/servicos' },
    { label: 'Contato', href: '/contato' },
  ];

  const getServiceName = (serviceId: string) => {
    const serviceNames: Record<string, string> = {
      '65da42048eca108d87c91da6': 'Agendar Videochamada',
      '65dcb7548eca108d87c91dbb': 'Avaliação',
      '65dcb7c58eca108d87c91dbd': 'Consignação',
      '65dcef468eca108d87c91dc8': 'Consórcio',
      '65dcb6528eca108d87c91db9': 'Despachante',
      '65dcb7898eca108d87c91dbc': 'Financiamento',
      '6628fd535bda2ae51f6fc5ff': 'Peças e Acessórios',
      '65dcef098eca108d87c91dc7': 'Revisões',
      '65dcb7238eca108d87c91dba': 'Seguros',
      '65dceeac8eca108d87c91dc6': 'Serviços',
      '65dcb7fe8eca108d87c91dbe': 'Tabela FIPE',
      '65dcb5e78eca108d87c91db8': 'Test-Drive',
      '65dcb8548eca108d87c91dbf': 'Vendas Diretas',
    };

    return serviceNames[serviceId] || 'Serviço';
  };

  const servicesList = store.services || [];

  return (
    <footer className="layout-3 wl3-footer text-white" style={{ backgroundColor: primaryColor }}>
      <div className="wl3-container py-12 md:py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <StoreFooterIcon
                config={config}
                store={store}
                alt={config.name}
                wrapperClassName="wl3-footer-logo shrink-0"
              />
              <div>
                <h3 className="font-bold text-lg">{config.name}</h3>
                {config.slogan && <p className="text-sm text-white/80">{config.slogan}</p>}
              </div>
            </div>
            <p className="text-sm text-white/80 leading-relaxed mb-5">
              Seminovos e zero KM com atendimento personalizado e as melhores condições do mercado.
            </p>

            {config.socialNetworks && (
              <div className="flex gap-2">
                {config.socialNetworks.instagram && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/10 hover:text-white rounded-full"
                    onClick={() => window.open(config.socialNetworks.instagram, '_blank')}
                    aria-label="Abrir Instagram"
                  >
                    <Instagram className="h-5 w-5" />
                  </Button>
                )}
                {config.socialNetworks.facebook && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/10 hover:text-white rounded-full"
                    onClick={() => window.open(config.socialNetworks.facebook, '_blank')}
                    aria-label="Abrir Facebook"
                  >
                    <Facebook className="h-5 w-5" />
                  </Button>
                )}
                {config.socialNetworks.youtube && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/10 hover:text-white rounded-full"
                    onClick={() => window.open(config.socialNetworks.youtube, '_blank')}
                    aria-label="Abrir YouTube"
                  >
                    <Youtube className="h-5 w-5" />
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-lg font-semibold mb-5">Navegação</h4>
            <ul className="space-y-2.5">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  {link.href.startsWith('#') ? (
                    <a href={link.href} className="text-white/80 hover:text-white transition-colors text-sm">
                      {link.label}
                    </a>
                  ) : (
                    <Link to={link.href} className="text-white/80 hover:text-white transition-colors text-sm">
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-lg font-semibold mb-5">Serviços</h4>
            <ul className="space-y-2.5">
              {servicesList.map((service: any, index: number) => (
                <li key={index}>
                  <button
                    onClick={() => setSelectedService(service)}
                    className="text-white/80 hover:text-white transition-colors text-sm text-left hover:underline underline-offset-4 cursor-pointer"
                  >
                    {getServiceName(service.service)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-lg font-semibold mb-5">Contato</h4>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="wl3-footer-icon">
                  <MapPin className="h-4 w-4" />
                </div>
                <p className="text-white/80 text-sm leading-relaxed">
                  {store?.place?.address || 'Endereço da empresa'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="wl3-footer-icon">
                  <Phone className="h-4 w-4" />
                </div>
                <a href={store.phone?.ios} className="text-white/80 hover:text-white transition-colors text-sm">
                  {store.phone?.formatted}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <div className="wl3-footer-icon">
                  <MessageCircle className="h-4 w-4" />
                </div>
                <button
                  onClick={() => openWhatsApp(store.whatsapp?.ios)}
                  className="text-white/80 hover:text-white transition-colors text-sm text-left"
                >
                  {store.whatsapp?.formatted}
                </button>
              </div>
              <div className="flex items-center gap-3">
                <div className="wl3-footer-icon">
                  <Mail className="h-4 w-4" />
                </div>
                <a href={`mailto:${config.email}`} className="text-white/80 hover:text-white transition-colors text-sm">
                  {config.email}
                </a>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-white/20">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="h-4 w-4 text-white" />
                <span className="font-semibold text-sm">Horário de Funcionamento</span>
              </div>
              <div className="space-y-1 text-sm">
                {openingHours.map((hour, index) => (
                  <div key={index} className="flex justify-between text-white/80">
                    <span>{hour.day}</span>
                    <span>{hour.time}</span>
                  </div>
                ))}
              </div>
            </div>

            {(hasGooglePlayLink || hasAppStoreLink) && (
              <div className="mt-8">
                <h4 className="text-lg font-semibold mb-4">Baixe nosso App</h4>
                <div className="flex gap-3">
                  {hasGooglePlayLink && (
                    <a href={config.googlePlay.url} target="_blank" rel="noopener noreferrer">
                      <img src={googlePlay} alt="Disponível no Google Play" width={135} height={40} />
                    </a>
                  )}
                  {hasAppStoreLink && (
                    <a href={appStoreUrl} target="_blank" rel="noopener noreferrer">
                      <img src={appStore} alt="Baixar na App Store" width={135} height={40} />
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-white/20">
        <div className="wl3-container py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/80">
            <p>&copy; {new Date().getFullYear()} {config.name}. Todos os direitos reservados.</p>

            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/politica-privacidade"
                className="text-white/80 hover:text-white transition-colors underline-offset-4 hover:underline"
              >
                Política de Privacidade
              </Link>
              <span className="hidden md:inline">•</span>
              <Link
                to="/termos-uso"
                className="text-white/80 hover:text-white transition-colors underline-offset-4 hover:underline"
              >
                Termos de Uso
              </Link>
            </div>
          </div>
        </div>
      </div>

      {selectedService && (
        <ServiceModal
          service={selectedService}
          config={config}
          isOpen={!!selectedService}
          onOpenChange={(open) => {
            if (!open) setSelectedService(null);
          }}
        />
      )}
    </footer>
  );
};
